import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material';
import { MoreVertical, Plus, SquarePen, Trash2 } from 'lucide-react';
import PageContainer from '../../common_components/PageContainer';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import AppButton from '../../common_components/AppButton';
import CommonTable from '../../common_components/CommonTable';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import AccessRestrictedContent from '../../common_components/AccessRestrictedContent';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import { SearchInput } from '../../common_components/FormInputs';
import { toast } from '../../common_components/Toast/ToastUtils';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import clockifyGoalService, { goalErrorMessage } from '../../services/clockifyGoal.service';
import ClockifyGoalDrawer from './ClockifyGoalDrawer';
/**
 * Clockify Goal (Goal Management) - the per-client weekly / monthly minutes target of each Clockify tag.
 * Employee Performance's Account Performance table reads these goals to show each task's target.
 *
 * Laid out like Create Report's listing (header bar with search + add button, bordered table, ⋮ row menu).
 *
 * Permissions: module access = view the list; the "Manage Goal" action adds Add Goal, Edit and Delete - the server
 * enforces the same rule. No client scope and no refresh.
 */
const MODULE_KEY = 'clockify_goal';
const MANAGE_GOAL = 'Manage Goal';
const textSx = { fontSize: '14px !important', color: '#344054' };
/** Goal Name and Notes are held at exactly these widths; longer text is cut with "..." and shown in a tooltip. */
const NAME_WIDTH = '280px';
const NOTES_WIDTH = '320px';
const fixedWidthSx = (width) => ({
    width,
    minWidth: width,
    maxWidth: width,
    overflow: 'hidden',
});
/** Text with an ellipsis; the tooltip appears only when the text is actually cut off. */
const TruncatedCell = ({ text, color }) => {
    const [truncated, setTruncated] = useState(false);
    if (!text)
        return _jsx(Typography, { sx: textSx, children: "-" });
    return (_jsx(HoverTooltip, { title: truncated ? _jsx(Box, { sx: { maxWidth: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, children: text }) : '', placement: "top", arrow: true, children: _jsx(Typography, { onMouseEnter: (e) => setTruncated(e.currentTarget.scrollWidth > e.currentTarget.clientWidth), sx: {
                ...textSx,
                ...(color ? { color } : {}),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }, children: text }) }));
};
/** Above 60 minutes -> hours, 200 -> "3.33 hrs" (2 decimals, no trailing zeros); 60 or less stays in minutes, 45 -> "45 mins". */
const formatDuration = (minutes) => {
    if (minutes > 60)
        return `${Math.round((minutes / 60) * 100) / 100} hrs`;
    return `${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
};
/** Minutes target; hover shows it as hours when above 60 minutes, otherwise in minutes. An empty target is "-" with no tooltip. */
const MinutesCell = ({ minutes }) => {
    if (minutes == null)
        return _jsx(Typography, { sx: textSx, children: "-" });
    return (_jsx(HoverTooltip, { title: formatDuration(minutes), placement: "top", arrow: true, children: _jsx(Typography, { sx: { ...textSx, display: 'inline-block', cursor: 'default' }, children: minutes }) }));
};
/** Monthly rate that differs by client type: one line per type, hover shows each as hours / minutes. */
const ClientTypeRatesCell = ({ rates }) => {
    const lines = [
        { label: 'Email', minutes: rates.email },
        { label: 'Non-email', minutes: rates.non_email },
    ];
    return (_jsx(HoverTooltip, { title: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.5 }, children: lines.map((l) => (_jsxs("span", { children: [l.label, ": ", l.minutes == null ? '-' : formatDuration(l.minutes)] }, l.label))) }), placement: "top", arrow: true, children: _jsx(Box, { sx: { display: 'inline-flex', flexDirection: 'column', gap: 0.25, cursor: 'default' }, children: lines.map((l) => (_jsxs(Typography, { sx: { ...textSx, whiteSpace: 'nowrap' }, children: [l.label, ": ", l.minutes == null ? '-' : l.minutes] }, l.label))) }) }));
};
/** Row ⋮ menu - same look as Create Report's; both entries are write actions, so it only exists with "Manage Goal". */
const RowActions = ({ row, onEdit, onDelete, }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClose = () => setAnchorEl(null);
    const wrap = (fn) => () => {
        handleClose();
        fn(row);
    };
    return (_jsxs(Box, { sx: { display: 'flex', justifyContent: 'center' }, onClick: (e) => e.stopPropagation(), children: [_jsx(IconButton, { "aria-label": "row actions", onClick: (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }, sx: {
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    color: '#344054',
                    border: `1px solid ${open ? '#FF008E' : '#D0D5DD'}`,
                    bgcolor: '#FFFFFF',
                    '&:hover': { bgcolor: '#FDF2F8', borderColor: '#FF008E' },
                }, children: _jsx(MoreVertical, { size: 18 }) }), _jsxs(Menu, { anchorEl: anchorEl, open: open, onClose: handleClose, transformOrigin: { horizontal: 'right', vertical: 'top' }, anchorOrigin: { horizontal: 'right', vertical: 'bottom' }, slotProps: {
                    paper: {
                        sx: {
                            minWidth: 200,
                            boxShadow: '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)',
                            border: '1px solid #EAECF0',
                            borderRadius: '12px',
                            mt: 0.5,
                            '& .MuiMenuItem-root': { py: 1.25, color: '#344054' },
                            '& .MuiListItemText-primary': { fontSize: '14px !important' },
                        },
                    },
                }, children: [_jsxs(MenuItem, { onClick: wrap(onEdit), children: [_jsx(ListItemIcon, { sx: { minWidth: 34, color: '#344054' }, children: _jsx(SquarePen, { size: 18 }) }), _jsx(ListItemText, { primary: "Edit Goal" })] }), _jsx(Divider, { sx: { my: '4px !important' } }), _jsxs(MenuItem, { onClick: wrap(onDelete), sx: { color: '#D92D20 !important' }, children: [_jsx(ListItemIcon, { sx: { minWidth: 34, color: '#D92D20' }, children: _jsx(Trash2, { size: 18 }) }), _jsx(ListItemText, { primary: "Delete Goal" })] })] })] }));
};
const columns = (canManage) => [
    {
        id: 'name',
        label: 'Goal Name',
        width: NAME_WIDTH,
        sortable: true,
        align: 'left',
        headerSx: fixedWidthSx(NAME_WIDTH),
        cellSx: () => fixedWidthSx(NAME_WIDTH),
    },
    { id: 'weekly_minutes_per_client', label: 'Weekly Minutes Per Client', width: '210px', sortable: true, align: 'left' },
    { id: 'monthly_minutes_per_client', label: 'Monthly Minutes Per Client', width: '220px', sortable: true, align: 'left' },
    {
        id: 'notes',
        label: 'Notes',
        width: NOTES_WIDTH,
        sortable: false,
        align: 'left',
        headerSx: fixedWidthSx(NOTES_WIDTH),
        cellSx: () => fixedWidthSx(NOTES_WIDTH),
    },
    ...(canManage ? [{ id: 'action', label: '', width: '80px', sortable: false, align: 'center' }] : []),
];
/** Empty targets sort last in both directions. */
const compareGoals = (a, b, columnId, direction) => {
    const sign = direction === 'asc' ? 1 : -1;
    if (columnId === 'name')
        return sign * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const av = a[columnId];
    const bv = b[columnId];
    if (av == null && bv == null)
        return 0;
    if (av == null)
        return 1;
    if (bv == null)
        return -1;
    return sign * (av - bv);
};
const ClockifyGoalMain = () => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);
    const permissions = useSelector((state) => state.auth.permissions);
    const modulePerms = permissions?.[MODULE_KEY];
    const hasAccess = modulePerms?.access !== false;
    const actions = Array.isArray(modulePerms?.actions) ? modulePerms.actions : [];
    const canManage = actions.includes(MANAGE_GOAL);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [sortConfig, setSortConfig] = useState({ columnId: '', direction: 'asc' });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // setActiveModuleKey is a plain action, so the key is in the store before the fetch reads it for the x-module-key header.
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
    }, [dispatch]);
    const loadGoals = useCallback(async () => {
        setLoading(true);
        try {
            const res = await clockifyGoalService.listGoals(token || '');
            if (res?.data?.success) {
                setGoals(res.data.data?.goals || []);
            }
            else {
                setGoals([]);
                toast.error(goalErrorMessage(res, 'Failed to load goals.'));
            }
        }
        catch {
            setGoals([]);
            toast.error('Failed to load goals.');
        }
        finally {
            setLoading(false);
        }
    }, [token]);
    useEffect(() => {
        if (!hasAccess)
            return;
        loadGoals();
    }, [hasAccess, loadGoals]);
    const handleSearch = useCallback((value) => setSearchText(value.trim()), []);
    const rows = useMemo(() => {
        const q = searchText.toLowerCase();
        const filtered = q ? goals.filter((g) => g.name.toLowerCase().includes(q) || g.notes.toLowerCase().includes(q)) : goals;
        if (!sortConfig.columnId)
            return filtered;
        return [...filtered].sort((a, b) => compareGoals(a, b, sortConfig.columnId, sortConfig.direction));
    }, [goals, searchText, sortConfig]);
    const openAdd = () => { setEditTarget(null); setDrawerOpen(true); };
    const openEdit = (goal) => { setEditTarget(goal); setDrawerOpen(true); };
    const handleDeleteConfirm = async () => {
        if (!deleteTarget)
            return;
        setDeleting(true);
        try {
            const res = await clockifyGoalService.deleteGoal(deleteTarget.id, token || '');
            if (res?.data?.success) {
                toast.success(res.data.message || 'Goal deleted successfully.');
                setDeleteTarget(null);
                loadGoals();
            }
            else {
                toast.error(goalErrorMessage(res, 'Could not delete the goal.'));
            }
        }
        catch {
            toast.error('Could not delete the goal.');
        }
        finally {
            setDeleting(false);
        }
    };
    const renderCell = (row, column) => {
        switch (column.id) {
            case 'name':
                return _jsx(TruncatedCell, { text: row.name, color: "#101828" });
            case 'weekly_minutes_per_client':
            case 'monthly_minutes_per_client':
                if (column.id === 'monthly_minutes_per_client' && row.monthly_minutes_by_client_type) {
                    return _jsx(ClientTypeRatesCell, { rates: row.monthly_minutes_by_client_type });
                }
                return _jsx(MinutesCell, { minutes: row[column.id] });
            case 'notes':
                return _jsx(TruncatedCell, { text: row.notes });
            case 'action':
                return _jsx(RowActions, { row: row, onEdit: openEdit, onDelete: setDeleteTarget });
            default:
                return null;
        }
    };
    if (!hasAccess) {
        return (_jsx(PageContainer, { children: _jsx(AccessRestrictedContent, {}) }));
    }
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    height: 'calc(100vh - 90px)',
                    overflow: 'hidden',
                }, children: [_jsx(PagesCommonHeader, { height: "58px", title: "Clockify Goal", rightSection: _jsxs(_Fragment, { children: [_jsx(Box, { sx: { width: 280 }, children: _jsx(SearchInput, { placeholder: "Search goals", onSearch: handleSearch, size: "sm", debounceMs: 400 }) }), canManage && (_jsx(AppButton, { variant: "contained", startIcon: _jsx(Plus, { size: 16 }), onClick: openAdd, className: "all-Font-Sizes", sx: {
                                        bgcolor: '#FF008E',
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                        '&:hover': { bgcolor: '#E6007A' },
                                    }, children: "Add Goal" }))] }) }), _jsx(Paper, { elevation: 0, sx: {
                            border: '1px solid #D0D5DD',
                            borderRadius: 2,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                        }, children: _jsx(Box, { sx: {
                                overflow: 'hidden',
                                flex: 1,
                                minHeight: 0,
                                // Give the table's scroll container a definite height so its maxHeight:100% resolves and
                                // the body scrolls internally, keeping the header row sticky.
                                '& .reusable-table-container': { height: '100%' },
                            }, children: _jsx(CommonTable, { columns: columns(canManage), rows: rows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: (columnId, direction) => setSortConfig(columnId && direction ? { columnId, direction } : { columnId: '', direction: 'asc' }), isLoading: loading, emptyMessage: searchText
                                    ? 'No goals match your search.'
                                    : canManage
                                        ? 'No goals yet - click "Add Goal" to create the first one.'
                                        : 'No goals have been created yet.', stickyHeader: true, expandTrigger: "none", maxHeight: "100%", pinnedColumns: { left: [], right: canManage ? ['action'] : [] } }) }) })] }), _jsx(ClockifyGoalDrawer, { open: drawerOpen, goal: editTarget, onClose: () => { setDrawerOpen(false); setEditTarget(null); }, onSaved: loadGoals }), _jsx(ConfirmActionModal, { open: !!deleteTarget, title: "Delete Goal", description: deleteTarget
                    ? `Delete the goal "${deleteTarget.name}"? Employee Performance will stop showing a target for this task. This cannot be undone.`
                    : '', confirmText: "Delete", onConfirm: handleDeleteConfirm, onClose: () => !deleting && setDeleteTarget(null), loading: deleting, isDeleteIcon: true })] }));
};
export default ClockifyGoalMain;
